import { useParams } from 'react-router-dom'
import { useRunWithDetails } from '../hooks/useRuns'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import { Badge } from '../components/ui/badge'
import { CourseTab } from '../components/run-builder/CourseTab'
import { PlanTab } from '../components/run-builder/PlanTab'
import { CalibrationTab } from '../components/run-builder/CalibrationTab'
import { ExportTab } from '../components/run-builder/ExportTab'
import { Map, Settings, ClipboardList, Download, Calendar, Route, Loader2, AlertCircle, FileX } from 'lucide-react'
import { formatDate } from '../lib/calculations'

export default function RunBuilder() {
  const { id } = useParams<{ id: string }>()
  const { data: run, isLoading, error } = useRunWithDetails(id!)

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <div className="container mx-auto px-6 py-12 max-w-7xl">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center space-y-6">
              <div className="w-16 h-16 mx-auto bg-primary-700 rounded-xl flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-semibold text-primary-900">Loading your run plan</h2>
                <p className="text-neutral-600">Preparing your ultra running adventure...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <div className="container mx-auto px-6 py-12 max-w-7xl">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center space-y-6 max-w-md">
              <div className="w-16 h-16 mx-auto bg-accent-600 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-white" />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-semibold text-primary-900">Unable to load run</h2>
                <p className="text-neutral-600">We encountered an error while loading your run plan: {error.message}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!run) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <div className="container mx-auto px-6 py-12 max-w-7xl">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center space-y-6">
              <div className="w-16 h-16 mx-auto bg-neutral-400 rounded-xl flex items-center justify-center">
                <FileX className="w-8 h-8 text-white" />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-semibold text-primary-900">Run not found</h2>
                <p className="text-neutral-600">The run plan you're looking for doesn't exist or has been deleted.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Calculate progress indicators
  const totalDistance = run.segments?.reduce((sum, segment) => sum + segment.distance_km, 0) || 0
  const segmentCount = run.segments?.length || 0
  const aidStationCount = run.aid_stations?.length || 0
  const planItemCount = run.plan_items?.length || 0
  const hasCalibration = !!run.calibration_profile_id

  const isPlanned = segmentCount > 0 && planItemCount > 0
  const isEmpty = segmentCount === 0 && aidStationCount === 0 && planItemCount === 0

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="container mx-auto px-6 py-12 max-w-7xl space-y-10">
        {/* Header */}
        <div className="bg-white rounded-2xl border border-neutral-200 shadow-minimal p-8">
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
            <div className="flex-1 space-y-4">
              <div>
                <h1 className="text-4xl font-bold text-primary-900 mb-3">
                  {run.title}
                </h1>
                {run.date && (
                  <div className="flex items-center text-neutral-600">
                    <Calendar className="h-5 w-5 mr-2 text-primary-600" />
                    <span className="text-lg font-medium">{formatDate(run.date)}</span>
                  </div>
                )}
              </div>

              {run.notes && (
                <div className="p-4 bg-neutral-50 rounded-xl border border-neutral-100">
                  <p className="text-neutral-700 italic leading-relaxed">"{run.notes}"</p>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-4">
              {/* Status Badge */}
              <div className="flex justify-end">
                {isPlanned && (
                  <Badge className="bg-emerald-600 text-white border-0 px-4 py-2 text-sm font-medium">
                    ✓ Plan Complete
                  </Badge>
                )}
                {isEmpty && (
                  <Badge variant="outline" className="text-primary-700 border-primary-300 bg-primary-50 px-4 py-2 text-sm font-medium">
                    Getting Started
                  </Badge>
                )}
                {!isEmpty && !isPlanned && (
                  <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium">
                    In Progress
                  </Badge>
                )}
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-3 lg:min-w-[280px]">
                <div className="flex items-center gap-3 p-3 bg-neutral-50 rounded-xl border border-neutral-100">
                  <div className="p-2 bg-primary-600 rounded-lg">
                    <Route className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <div className="text-xs font-medium text-primary-700 uppercase tracking-wide">Distance</div>
                    <div className="font-bold text-primary-900">
                      {totalDistance > 0 ? `${totalDistance.toFixed(1)}km` : '—'}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-neutral-50 rounded-xl border border-neutral-100">
                  <div className="p-2 bg-violet-600 rounded-lg">
                    <Map className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <div className="text-xs font-medium text-violet-700 uppercase tracking-wide">Segments</div>
                    <div className="font-bold text-primary-900">{segmentCount}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="course" className="space-y-8">
          <div className="bg-white rounded-2xl border border-neutral-200 shadow-minimal p-2">
            <TabsList className="grid w-full grid-cols-4 bg-neutral-50 gap-1 p-1">
              <TabsTrigger
                value="course"
                className="flex items-center gap-2 px-6 py-3 text-neutral-600 bg-transparent hover:bg-white data-[state=active]:bg-white data-[state=active]:text-primary-700 data-[state=active]:shadow-minimal rounded-lg transition-all duration-200 font-medium"
              >
                <Map className="h-4 w-4" />
                Course
              </TabsTrigger>
              <TabsTrigger
                value="calibration"
                className="flex items-center gap-2 px-6 py-3 text-neutral-600 bg-transparent hover:bg-white data-[state=active]:bg-white data-[state=active]:text-primary-700 data-[state=active]:shadow-minimal rounded-lg transition-all duration-200 font-medium"
              >
                <Settings className="h-4 w-4" />
                Calibration
                {hasCalibration && <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>}
              </TabsTrigger>
              <TabsTrigger
                value="plan"
                className="flex items-center gap-2 px-6 py-3 text-neutral-600 bg-transparent hover:bg-white data-[state=active]:bg-white data-[state=active]:text-primary-700 data-[state=active]:shadow-minimal rounded-lg transition-all duration-200 font-medium"
              >
                <ClipboardList className="h-4 w-4" />
                Plan
                {planItemCount > 0 && <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>}
              </TabsTrigger>
              <TabsTrigger
                value="export"
                className="flex items-center gap-2 px-6 py-3 text-neutral-600 bg-transparent hover:bg-white data-[state=active]:bg-white data-[state=active]:text-primary-700 data-[state=active]:shadow-minimal rounded-lg transition-all duration-200 font-medium"
              >
                <Download className="h-4 w-4" />
                Export
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Tab Content */}
          <TabsContent value="course" className="space-y-6">
            <div className="bg-white rounded-2xl border border-neutral-200 shadow-minimal overflow-hidden">
              <CourseTab run={run} />
            </div>
          </TabsContent>

          <TabsContent value="calibration" className="space-y-6">
            <div className="bg-white rounded-2xl border border-neutral-200 shadow-minimal overflow-hidden">
              <CalibrationTab run={run} />
            </div>
          </TabsContent>

          <TabsContent value="plan" className="space-y-6">
            <div className="bg-white rounded-2xl border border-neutral-200 shadow-minimal overflow-hidden">
              <PlanTab run={run} />
            </div>
          </TabsContent>

          <TabsContent value="export" className="space-y-6">
            <div className="bg-white rounded-2xl border border-neutral-200 shadow-minimal overflow-hidden">
              <ExportTab run={run} />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}